"use client";

import { Routes } from "@/api";
import { updateThemeAction } from "@/app/actions/user/user-actions";
import DesktopOnly from "@/components/desktop-only";
import MobileOnly from "@/components/mobile-only";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useMeasure from "@/hooks/layout/use-measure";
import { useToggle } from "@/hooks/use-toggle";
import { PropsWithClassName } from "@/types";
import {
  LayoutDashboardIcon,
  MenuIcon,
  MoonStarIcon,
  SunIcon,
  XIcon,
} from "lucide-react";
import { AnimatePresence, motion, MotionConfig, Variants } from "motion/react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import Link from "next/link";
import React, { PropsWithChildren } from "react";

const variants: Variants = {
  initial: {
    opacity: 0,
    scale: 0,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0,
  },
};

export function PublicHeader({ children }: PropsWithChildren) {
  const menuOpenToggle = useToggle();
  const [ref, measure] = useMeasure<HTMLDivElement>();

  const toggleMenu = () => menuOpenToggle.toggle();

  return (
    <header
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      ref={ref}
    >
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-2">
          {children}
          <Badge variant="secondary" className="select-none hover:bg-secondary">
            Beta
          </Badge>
        </div>
        {/* Desktop Navigation */}
        <DesktopOnly>
          <MotionConfig transition={{ duration: 0.4 }}>
            <LinksContainer className="flex gap-6">
              <Links />
            </LinksContainer>
          </MotionConfig>
        </DesktopOnly>
        {/* Desktop CTA */}
        <DesktopOnly>
          <span className="flex items-center gap-2">
            <ThemeTrigger />
            <DesktopAuthButtons />
          </span>
        </DesktopOnly>

        {/* Mobile Menu Button */}
        <MobileOnly>
          <span className="flex items-center gap-2">
            <ThemeTrigger />
            <MobileMenuTrigger
              onClick={toggleMenu}
              open={menuOpenToggle.value}
            />
          </span>
        </MobileOnly>
      </div>
      {/* Mobile Menu */}
      <MobileOnly>
        <AnimatePresence mode="sync">
          {menuOpenToggle.value ? (
            <MobileMenu
              key={"mobile-menu"}
              height={measure.height}
              onLinkClick={menuOpenToggle.setOff}
            >
              <MobileAuthButtons onClick={menuOpenToggle.setOff} />
            </MobileMenu>
          ) : null}
        </AnimatePresence>
      </MobileOnly>
    </header>
  );
}

function DesktopAuthButtons() {
  const t = useTranslations("Header");
  // const tAuth = useTranslations("Header.auth");
  const { data: session } = useSession();

  if (session?.user.id) {
    return (
      <Link href={Routes.USER_PORTFOLIOS(session.user.id)}>
        <Button size="sm" className="flex items-center gap-2 !rounded-full">
          <LayoutDashboardIcon className="size-4" />
          {t("dashboard")}
        </Button>
      </Link>
    );
  }

  return null;

  // return (
  //   <span className="flex items-center gap-4">
  //     <Link href={Routes.AUTHENTICATION}>
  //       <Button variant="ghost" size="sm">
  //         {tAuth("logIn")}
  //       </Button>
  //     </Link>
  //     <Link href={Routes.AUTHENTICATION}>
  //       <Button size="sm">{tAuth("signUp")}</Button>
  //     </Link>
  //   </span>
  // );
}

function LinksContainer(props: PropsWithChildren<PropsWithClassName>) {
  return (
    <motion.nav
      initial="hidden"
      animate="visible"
      exit="hidden"
      className={props.className}
      variants={{
        visible: {
          transition: {
            staggerChildren: 0.08,
          },
        },
        hidden: {},
      }}
    >
      {props.children}
    </motion.nav>
  );
}

const linkVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
  },
};

function Links(props: { onLinkClick?: () => void }) {
  const t = useTranslations("Header");

  return (
    <MotionConfig transition={{ duration: 0.4 }}>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={Routes.HOME}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("home")}
        </Link>
      </motion.div>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={`${Routes.HOME}#features`}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("features")}
        </Link>
      </motion.div>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={`${Routes.HOME}#pricing`}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("pricing")}
        </Link>
      </motion.div>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={Routes.BLOG}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("blog")}
        </Link>
      </motion.div>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={Routes.ABOUT}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("about")}
        </Link>
      </motion.div>
      <motion.div variants={linkVariants} initial="hidden" animate="visible">
        <Link
          href={Routes.CONTACT}
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={props.onLinkClick}
        >
          {t("contact")}
        </Link>
      </motion.div>
    </MotionConfig>
  );
}

function MobileAuthButtons(props: { onClick: () => void }) {
  const t = useTranslations("Header");
  // const tAuth = useTranslations("Header.auth");
  const { data: session } = useSession();

  if (session?.user.id) {
    return (
      <Link href={Routes.USER_PORTFOLIOS(session.user.id)} className="w-full">
        <Button className="flex w-full items-center gap-2 !rounded-full">
          <LayoutDashboardIcon className="size-4" />
          {t("dashboard")}
        </Button>
      </Link>
    );
  }

  return null;

  // return (
  //   <div className="flex items-center gap-4">
  //     <Link href={Routes.AUTHENTICATION} className="w-full">
  //       <Button
  //         variant="outline"
  //         size="sm"
  //         className="w-full !rounded-full"
  //         onClick={props.onClick}
  //       >
  //         {tAuth("logIn")}
  //       </Button>
  //     </Link>
  //     <Link href={Routes.AUTHENTICATION} className="w-full">
  //       <Button
  //         size="sm"
  //         className="w-full !rounded-full"
  //         onClick={props.onClick}
  //       >
  //         {tAuth("signUp")}
  //       </Button>
  //     </Link>
  //   </div>
  // );
}

function MobileMenuTrigger(props: { onClick: () => void; open: boolean }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={props.onClick}
      aria-label="Toggle menu"
    >
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence mode="popLayout">
          {props.open ? (
            <motion.div
              key="close-menu"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <XIcon className="size-5" />
            </motion.div>
          ) : (
            <motion.div
              key="open-menu"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <MenuIcon className="size-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </MotionConfig>
    </Button>
  );
}

function MobileMenu(
  props: PropsWithChildren<{ onLinkClick?: () => void; height: number }>,
) {
  return (
    <MotionConfig transition={{ duration: 0.4, bounce: 0 }}>
      <motion.div
        className="container fixed flex h-screen w-full flex-col gap-6 overflow-hidden border-t bg-background py-4 shadow-md"
        style={{
          top: `${props.height}px`,
        }}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0.3 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        layout="size"
      >
        <>
          {props.children}
          <LinksContainer className="flex flex-col space-y-4">
            <Links onLinkClick={props.onLinkClick} />
          </LinksContainer>
        </>
      </motion.div>
    </MotionConfig>
  );
}

function ThemeTrigger() {
  const { setTheme, theme } = useTheme();

  const onThemeClick = React.useCallback(async () => {
    let newTheme = "light";
    if (theme === "light") {
      newTheme = "dark";
    }
    setTheme(newTheme);
    await updateThemeAction(newTheme);
  }, [setTheme, theme]);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onThemeClick}
      aria-label="Toggle theme"
    >
      <SunIcon className="size-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <MoonStarIcon className="absolute size-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
    </Button>
  );
}
